import { useNavigate, useRouterState } from '@tanstack/react-router';
import {
  DropdownMenu,
  IcnGlobe,
  PrimitiveButton,
  type MenuEntry,
  type MenuItemDescriptor,
} from '@viquard/doc-ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useGroupsList } from '@/features/groups/api';
import { localiseSystemGroupName } from '@/features/groups/system-groups';

export interface GroupSwitcherProps {
  activeSlug?: string | null | undefined;
  onActiveSlugChange?: (slug: string | null) => void;
}

/** Compact pill-style group switcher in the topbar. Mirrors DEMO/shell.jsx
 *  GroupSwitcher: coloured dot + active group name + caret. Selecting a
 *  group navigates to its detail page; "All groups" clears the active
 *  scope and stays on the current route. */
export function GroupSwitcher({ activeSlug, onActiveSlugChange }: GroupSwitcherProps): ReactNode {
  const { t } = useTranslation('common');
  const groups = useGroupsList({ filter: 'active' });
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const articleSearch = useRouterState({
    select: (state) => state.location.search as {
      categoryId?: string;
      tagId?: string;
      groupId?: string;
    },
  });
  const memberGroups = (groups.data?.groups ?? []).filter((g) => g.viewerRole !== null);
  const isArticlesIndex = pathname === '/articles' || pathname === '/articles/';

  const articleSearchWithGroup = (groupId: string | null): {
    categoryId?: string;
    tagId?: string;
    groupId?: string;
  } => {
    const next: { categoryId?: string; tagId?: string; groupId?: string } = {};
    if (typeof articleSearch.categoryId === 'string') next.categoryId = articleSearch.categoryId;
    if (typeof articleSearch.tagId === 'string') next.tagId = articleSearch.tagId;
    if (groupId !== null) next.groupId = groupId;
    return next;
  };

  const active =
    activeSlug !== undefined && activeSlug !== null
      ? memberGroups.find((g) => g.slug === activeSlug) ?? null
      : null;

  const items: MenuEntry[] = [];
  items.push({
    kind: 'item',
    id: 'all',
    label: t('groupSwitcher.all'),
    icon: <IcnGlobe size={14} />,
    onSelect: () => {
      onActiveSlugChange?.(null);
      if (isArticlesIndex) {
        void navigate({ to: '/articles', search: articleSearchWithGroup(null) });
      }
    },
  });
  if (memberGroups.length > 0) {
    items.push({ kind: 'separator', id: 'sep' });
  }
  for (const g of memberGroups) {
    const item: MenuItemDescriptor = {
      kind: 'item',
      id: g.slug,
      label: localiseSystemGroupName(g, t),
      icon: (
        <span
          aria-hidden
          className="inline-block h-3 w-3 flex-shrink-0"
          style={{ background: g.color, borderRadius: 'var(--vq-radius-pill)' }}
        />
      ),
      onSelect: () => {
        onActiveSlugChange?.(g.slug);
        if (isArticlesIndex) {
          void navigate({ to: '/articles', search: articleSearchWithGroup(g.id) });
        } else if (!pathname.startsWith('/articles')) {
          void navigate({ to: '/groups/$slug', params: { slug: g.slug } });
        }
      },
    };
    items.push(item);
  }

  return (
    <DropdownMenu items={items}>
      <PrimitiveButton
        aria-label={t('groupSwitcher.label')}
        className="topbar-group-switcher inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm transition-colors"
        style={{
          background: 'var(--vq-paper-sunk)',
          border: '1px solid var(--vq-rule)',
          color: 'var(--vq-ink-2)',
          cursor: 'pointer',
          maxWidth: 220,
        }}
      >
        {active === null ? (
          <span
            aria-hidden
            className="inline-flex h-4 w-4 items-center justify-center"
            style={{ color: 'var(--vq-ink-3)' }}
          >
            <IcnGlobe size={12} />
          </span>
        ) : (
          <span
            aria-hidden
            className="inline-block h-4 w-4 flex-shrink-0"
            style={{ background: active.color, borderRadius: 'var(--vq-radius-pill)' }}
          />
        )}
        <span className="truncate font-medium" style={{ fontSize: 13 }}>
          {active === null ? t('groupSwitcher.all') : localiseSystemGroupName(active, t)}
        </span>
      </PrimitiveButton>
    </DropdownMenu>
  );
}
